import { useTranslation } from 'react-i18next';
import { useOrganizationUpgrade } from 'features/organizations/hooks/useOrganizationUpgrade';
import { Grid2, Typography } from '@mui/material';
import { formatToUSDollars } from 'shared/utils';
import { OrganizationUpgrade } from 'features/organizations/organizations.types';
import { useOrganizationDetails } from 'features/organizations/hooks/useOrganizationDetails';
import { LoadingAndErrors } from 'components/LoadingAndErrors';
import { OrganizationSeatsSummary } from '../OrganizationSeatsSummary';

interface OrganizationUpgradeSummaryProps {
  regularSeats: number;
  proSeats: number;
}

const SummaryRow = ({
  label,
  value,
  fontWeight = 500,
}: {
  label: string;
  value: string | number;
  fontWeight?: number;
}): JSX.Element => (
  <Grid2 container justifyContent="space-between">
    <Typography style={{ textAlign: 'right', flex: 2 }} fontWeight={fontWeight}>
      {label}
    </Typography>
    <Typography style={{ textAlign: 'right', flex: 1 }} fontWeight={fontWeight}>
      {value}
    </Typography>
  </Grid2>
);

const displayNumber = (num: number | undefined): string => {
  if (num === undefined) {
    return '-';
  }
  if (num < 0) {
    return `(${formatToUSDollars(-num, 2)})`;
  }

  return formatToUSDollars(num || 0, 2);
};

export const OrganizationUpgradeSummary = (
  props: OrganizationUpgradeSummaryProps,
): JSX.Element | null => {
  const { regularSeats = 0, proSeats = 0 } = props;
  const { t } = useTranslation();
  const {
    details,
    isLoading: detailsLoading,
    isError: detailsError,
  } = useOrganizationDetails();

  const upgradeData =
    (regularSeats || proSeats) > 0
      ? {
          regularSeats:
            (details?.seats.regular.purchased || 0) + (regularSeats || 0),
          proSeats: (details?.seats.pro.purchased || 0) + (proSeats || 0),
        }
      : undefined;

  const { previewUpgrade, isLoading, isError } = useOrganizationUpgrade({
    previewOrganizationUpgrade: upgradeData,
  });

  const summary: Partial<OrganizationUpgrade> = previewUpgrade || {};

  if (isLoading || isError || detailsLoading || detailsError) {
    return <LoadingAndErrors isLoading={isLoading || detailsLoading} />;
  }
  if (!(proSeats || regularSeats)) {
    return null;
  }

  const updatedSeats = {
    pro: {
      purchased: (details?.seats.pro.purchased || 0) + proSeats,
      used: details?.seats.pro.used || 0,
    },
    regular: {
      purchased: (details?.seats.regular.purchased || 0) + regularSeats,
      used: details?.seats.regular.used || 0,
    },
  };

  return (
    <Grid2 container direction="column" spacing={2}>
      <Grid2>
        <Typography variant="h2">{t('organizationPurchase.total')}</Typography>
      </Grid2>
      <Grid2>
        <OrganizationSeatsSummary seats={updatedSeats} showUsed={false} />
      </Grid2>
      <Grid2>
        <SummaryRow
          label={t('organizationPurchase.subtotal')}
          value={displayNumber(summary.subtotal)}
        />
        <SummaryRow
          label={t('organizationPurchase.savings')}
          value={displayNumber(summary.savings)}
        />
        <SummaryRow
          label={t('organizationPurchase.total')}
          value={displayNumber(summary.total)}
          fontWeight={700}
        />
      </Grid2>
      <Grid2>
        <SummaryRow
          label={t('organizationPurchase.credit')}
          value={displayNumber(summary.credit)}
        />
        <SummaryRow
          label={t('organizationPurchase.charge')}
          value={displayNumber(summary.charge)}
          fontWeight={700}
        />
      </Grid2>
    </Grid2>
  );
};
